import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Users, ChefHat, Activity, ArrowLeft, Trash2, Search, ShieldOff, Bookmark, Droplets, HeartPulse, Scale, Download,
} from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const [access, setAccess] = useState(null);
  const [view, setView] = useState("list");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    api.get("/admin/me").then((r) => setAccess(r.data.is_admin)).catch(() => setAccess(false));
  }, []);

  if (access === null) return <div className="p-10 text-slate-500">Loading…</div>;
  if (access === false) {
    return (
      <div className="p-10 max-w-2xl mx-auto">
        <Card className="rounded-3xl border-red-100 bg-red-50/40 p-8 text-center" data-testid="admin-forbidden">
          <ShieldOff className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <h2 className="font-display text-xl font-bold text-slate-900">Admin access required</h2>
          <p className="text-sm text-slate-500 mt-2">
            Your email is not on the admin list. Ask the owner to add it to <code className="bg-red-50 px-1 rounded">ADMIN_EMAILS</code> in backend env.
          </p>
        </Card>
      </div>
    );
  }

  if (view === "detail" && selectedId) {
    return <UserDetail id={selectedId} onBack={() => setView("list")} />;
  }
  return <AdminOverview onOpen={(id) => { setSelectedId(id); setView("detail"); }} />;
}

function StatCard({ label, value, Icon, color = "emerald" }) {
  return (
    <Card className={`rounded-2xl border-${color}-100 p-4 bg-white`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl bg-${color}-50 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
        <div>
          <div className="text-xs text-slate-400 uppercase tracking-wider">{label}</div>
          <div className="font-display text-2xl font-bold text-slate-900">{value}</div>
        </div>
      </div>
    </Card>
  );
}

function AdminOverview({ onOpen }) {
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [offset, setOffset] = useState(0);
  const LIMIT = 25;
  const [exporting, setExporting] = useState(false);

  const exportDb = async () => {
    setExporting(true);
    try {
      const r = await api.get("/admin/export/db", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const disp = r.headers?.["content-disposition"] || "";
      const m = disp.match(/filename="?([^"]+)"?/);
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1] : `nutriguide_db_export.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("DB export downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally { setExporting(false); }
  };

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/admin/users", { params: { q, limit: LIMIT, offset } }).then((r) => setData(r.data));
    }, 200);
    return () => clearTimeout(t);
  }, [q, offset]);

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <div className="mb-8 fade-up flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs text-emerald-700 font-semibold tracking-wider uppercase">Owner tools</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mt-1">Admin dashboard</h1>
          <p className="text-slate-500 mt-2">Full visibility into user accounts, plans, and tracking data.</p>
        </div>
        <Button
          onClick={exportDb}
          disabled={exporting}
          variant="outline"
          className="rounded-full border-emerald-200 hover:bg-emerald-50"
          data-testid="admin-export-db-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          {exporting ? "Exporting…" : "Export DB (JSON)"}
        </Button>
      </div>

      {stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="admin-stats">
          <StatCard label="Total users" value={stats.users} Icon={Users} />
          <StatCard label="Health profiles" value={stats.profiles} Icon={Users} color="sky" />
          <StatCard label="Saved plans" value={stats.saved_plans} Icon={Bookmark} />
          <StatCard label="LLM calls today" value={stats.meal_plan_generations_today} Icon={ChefHat} color="amber" />
          <StatCard label="Weight logs" value={stats.weight_logs} Icon={Scale} />
          <StatCard label="Glucose logs" value={stats.glucose_logs} Icon={Droplets} color="sky" />
          <StatCard label="BP logs" value={stats.bp_logs} Icon={HeartPulse} color="red" />
          <StatCard label="Reminders" value={stats.reminders} Icon={Activity} color="sky" />
        </div>
      )}

      <Card className="rounded-2xl border-emerald-100 p-4 mb-4 bg-white">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search users by email or name…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setOffset(0); }}
            className="pl-10 h-11 rounded-xl"
            data-testid="admin-search"
          />
        </div>
      </Card>

      <div className="space-y-2" data-testid="admin-user-list">
        {data.items.length === 0 && (
          <div className="text-center text-sm text-slate-400 py-8">No users match.</div>
        )}
        {data.items.map((u) => (
          <Card
            key={u.id}
            className="rounded-2xl border-emerald-100 p-4 bg-white flex items-center gap-4 cursor-pointer hover:shadow-md transition-all"
            onClick={() => onOpen(u.id)}
            data-testid={`admin-user-${u.id}`}
          >
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center font-semibold text-emerald-700">
              {(u.name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-display font-semibold text-slate-900 truncate">{u.name}</span>
                {u.email_verified && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 rounded-full text-[10px]">Verified</Badge>}
                {!u.has_profile && <Badge className="bg-amber-50 text-amber-700 border-amber-100 rounded-full text-[10px]">No profile</Badge>}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{u.email}</div>
            </div>
            <div className="text-right text-xs text-slate-500">
              <div>{u.saved_plans} saved plans</div>
              <div>joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</div>
            </div>
          </Card>
        ))}
      </div>

      {data.total > LIMIT && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <Button variant="outline" disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - LIMIT))} className="rounded-full">Prev</Button>
          <span className="text-sm text-slate-500">{offset + 1}–{Math.min(offset + LIMIT, data.total)} of {data.total}</span>
          <Button variant="outline" disabled={offset + LIMIT >= data.total} onClick={() => setOffset((o) => o + LIMIT)} className="rounded-full">Next</Button>
        </div>
      )}
    </div>
  );
}

function UserDetail({ id, onBack }) {
  const [d, setD] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/admin/users/${id}`).then((r) => setD(r.data));
  }, [id]);

  const del = async () => {
    if (!window.confirm("Permanently delete this user and ALL their data?")) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User deleted");
      onBack();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setDeleting(false); }
  };

  if (!d) return <div className="p-10 text-slate-500">Loading…</div>;
  const { user, profile, counts, saved_plans, current_daily_plan, current_weekly_plan, meal_plan_usage_today } = d;
  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4" data-testid="admin-back">
        <ArrowLeft className="w-4 h-4" /> Back to users
      </button>

      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">{user.name}</h1>
          <p className="text-sm text-slate-500 mt-1">{user.email} · joined {new Date(user.created_at).toLocaleDateString()}</p>
        </div>
        <Button variant="outline" onClick={del} disabled={deleting} className="rounded-full text-red-600 border-red-200 hover:bg-red-50" data-testid="admin-delete-user">
          <Trash2 className="w-4 h-4 mr-2" /> {deleting ? "Deleting…" : "Delete user"}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <Card className="rounded-2xl border-emerald-100 p-5">
          <h3 className="font-display font-semibold text-slate-900 mb-3">Profile</h3>
          {!profile && <p className="text-sm text-slate-500">No profile set.</p>}
          {profile && (
            <dl className="space-y-1.5 text-sm">
              <Row label="Age / Sex" value={`${profile.age} · ${profile.sex}`} />
              <Row label="Height / Weight" value={`${profile.height_cm} cm / ${profile.weight_kg} kg`} />
              <Row label="Conditions" value={(profile.conditions || []).join(", ") || "—"} />
              <Row label="Medications" value={(profile.medications || []).map((m) => m.name || m.brand_name).join(", ") || "—"} />
              <Row label="Allergies" value={[...(profile.allergies || []), ...(profile.custom_allergies || [])].join(", ") || "—"} />
              <Row label="Excluded" value={(profile.excluded_ingredients || []).join(", ") || "—"} />
              <Row label="Dietary" value={(profile.dietary_preferences || []).join(", ") || "—"} />
              <Row label="Units" value={profile.units || "metric"} />
            </dl>
          )}
        </Card>

        <Card className="rounded-2xl border-emerald-100 p-5">
          <h3 className="font-display font-semibold text-slate-900 mb-3">Activity counts</h3>
          <dl className="space-y-1.5 text-sm">
            <Row label="LLM calls today" value={`${meal_plan_usage_today} / 2`} />
            <Row label="Weight logs" value={counts.weight_logs} />
            <Row label="Glucose logs" value={counts.glucose_logs} />
            <Row label="BP logs" value={counts.bp_logs} />
            <Row label="Reminders" value={counts.reminders} />
            <Row label="Meal adherence" value={counts.adherence_meals} />
            <Row label="Daily notes" value={counts.adherence_notes} />
          </dl>
        </Card>
      </div>

      <Card className="rounded-2xl border-emerald-100 p-5 mb-4">
        <h3 className="font-display font-semibold text-slate-900 mb-3">Saved plans ({saved_plans.length})</h3>
        {saved_plans.length === 0 ? (
          <p className="text-sm text-slate-400">No saved plans.</p>
        ) : (
          <div className="space-y-2">
            {saved_plans.map((s) => (
              <div key={s.id} className="flex items-center justify-between p-3 rounded-xl bg-emerald-50/40">
                <div>
                  <div className="font-medium text-slate-900 text-sm">{s.name}</div>
                  <div className="text-xs text-slate-500">{s.plan_type} · {new Date(s.created_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="rounded-2xl border-emerald-100 p-5">
          <h3 className="font-display font-semibold text-slate-900 mb-2">Current daily plan</h3>
          {current_daily_plan ? (
            <>
              <div className="text-sm text-slate-700">
                <b>Breakfast:</b> {current_daily_plan.breakfast?.name || "—"}<br/>
                <b>Lunch:</b> {current_daily_plan.lunch?.name || "—"}<br/>
                <b>Dinner:</b> {current_daily_plan.dinner?.name || "—"}
              </div>
              {current_daily_plan._generated_at && <div className="text-xs text-slate-400 mt-2">Generated {new Date(current_daily_plan._generated_at).toLocaleString()}</div>}
            </>
          ) : <p className="text-sm text-slate-400">None</p>}
        </Card>
        <Card className="rounded-2xl border-emerald-100 p-5">
          <h3 className="font-display font-semibold text-slate-900 mb-2">Current weekly plan</h3>
          {current_weekly_plan?.days?.length ? (
            <div className="text-sm text-slate-700">
              {current_weekly_plan.days.length} days · {current_weekly_plan.total_weekly_calories || "?"} kcal total
              {current_weekly_plan._generated_at && <div className="text-xs text-slate-400 mt-2">Generated {new Date(current_weekly_plan._generated_at).toLocaleString()}</div>}
            </div>
          ) : <p className="text-sm text-slate-400">None</p>}
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-slate-500 flex-shrink-0">{label}</dt>
      <dd className="text-slate-900 font-medium text-right truncate">{value}</dd>
    </div>
  );
}
